import { useEffect, useRef, useState } from "react";
import { useAdminSystem, useAdminServiceResources, useAdminSystemResources } from "../hooks/useApi";
import "../admin-theme.css";

interface ServiceInfo {
  key: string;
  name: string;
  icon: React.ReactNode;
}

interface ServiceHealth {
  status: string;
  label: string;
  uptime?: string | null;
  uptime_seconds?: number | null;
  container_count?: number;
  message?: string | null;
}

interface ServiceResources {
  service: string;
  running: boolean;
  container_count: number;
  cpu_percent: number;
  memory_used_bytes: number;
  memory_limit_bytes: number;
  memory_percent?: number | null;
  memory_used?: string | null;
  memory_limit?: string | null;
  network_rx_bytes: number;
  network_tx_bytes: number;
  block_read_bytes: number;
  block_write_bytes: number;
  pids: number;
  message?: string | null;
}

interface TotalResources extends ServiceResources {
  total_container_count: number;
}

interface SystemInfo {
  version: string;
  status: string;
  services: Record<string, ServiceHealth>;
}

const SERVICE_ICON_SIZE = 18;

const services: ServiceInfo[] = [
  {
    key: "backend",
    name: "وب سرور",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>,
  },
  {
    key: "database",
    name: "پایگاه داده",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>,
  },
  {
    key: "redis",
    name: "Redis",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6c-1.5 0-2 .9-2 2s.5 2 2 2 2-.9 2-2-.5-2-2-2z" /><path d="M4 14c-1.5 0-2 .9-2 2s.5 2 2 2 2-.9 2-2-.5-2-2-2z" /><path d="M6 8h12c1.5 0 2.5 1 2.5 2.5S19.5 13 18 13H8" /><path d="M16 11V8" /></svg>,
  },
  {
    key: "celery",
    name: "Celery Worker",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h4M6 14h2M14 10h4M16 14h2" /></svg>,
  },
  {
    key: "celery-critical",
    name: "Celery Critical",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h4M6 14h2M14 10h4M16 14h2" /><path d="M12 2l2 2-2 2-2-2z" /></svg>,
  },
  {
    key: "celery-recovery",
    name: "Celery Recovery",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h4M6 14h2M14 10h4M16 14h2" /><path d="M12 2v4M10 4l2-2 2 2" /></svg>,
  },
  {
    key: "celery-beat",
    name: "Celery Beat",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  },
  {
    key: "nginx",
    name: "Nginx",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 3L2 8l10 5 10-5-10-5z" /><path d="M2 8v6l10 5 10-5V8" /></svg>,
  },
  {
    key: "frontend",
    name: "Frontend",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>,
  },
  {
    key: "db-backup",
    name: "DB Backup",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /><path d="M12 13v6" /><path d="M9 16l3 3 3-3" /></svg>,
  },
  {
    key: "clamav",
    name: "ClamAV",
    icon: <svg width={SERVICE_ICON_SIZE} height={SERVICE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-4z" /><path d="M9 12l2 2 4-4" /></svg>,
  },
];

const statusColor = (status: string) => {
  switch (status) {
    case "healthy": return { bg: "var(--admin-success-bg)", text: "var(--admin-success)", dot: "var(--admin-success)" };
    case "warning": return { bg: "var(--admin-warning-bg)", text: "var(--admin-warning)", dot: "var(--admin-warning)" };
    case "error": return { bg: "var(--admin-error-bg)", text: "var(--admin-error)", dot: "var(--admin-error)" };
    default: return { bg: "var(--admin-bg)", text: "var(--admin-text-secondary)", dot: "var(--admin-text-tertiary)" };
  }
};

const overallStatusLabel = (status: string) => {
  switch (status) {
    case "healthy": return { label: "فعال", color: "var(--admin-success)" };
    case "warning": return { label: "نیاز به بررسی", color: "var(--admin-warning)" };
    case "error": return { label: "ناسالم", color: "var(--admin-error)" };
    default: return { label: "نامشخص", color: "var(--admin-text-tertiary)" };
  }
};

const sseUrl = (serviceKey: string) =>
  `${import.meta.env.VITE_API_URL || ""}/api/admin/system/${encodeURIComponent(serviceKey)}/logs/stream`;

const toPersianDigits = (input: string | number): string =>
  String(input).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const formatBytes = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let amount = value;
  let index = 0;
  while (amount >= 1024 && index < units.length - 1) {
    amount /= 1024;
    index += 1;
  }
  const number = amount.toFixed(1).replace(/\.0$/, "");
  return `${toPersianDigits(number)} ${units[index]}`;
};

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  return `${toPersianDigits(rounded)}٪`;
};

function MetricBar({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: "var(--admin-border)" }}>
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{
          width: `${clamped}%`,
          background: "linear-gradient(90deg, var(--admin-primary), var(--admin-primary-light))",
        }}
      />
    </div>
  );
}

function MetricCard({
  label,
  value,
  percent,
  hint,
}: {
  label: string;
  value: string;
  percent?: number | null;
  hint?: string;
}) {
  return (
    <div className="admin-card p-4">
      <p className="text-xs font-medium mb-1" style={{ color: "var(--admin-text-tertiary)" }}>{label}</p>
      <p className="text-lg font-bold" style={{ color: "var(--admin-text-primary)" }}>{value}</p>
      {typeof percent === "number" && <MetricBar percent={percent} />}
      {hint && <p className="mt-1 text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>{hint}</p>}
    </div>
  );
}

function PairCard({ label, rows }: { label: string; rows: { key: string; value: string }[] }) {
  return (
    <div className="admin-card p-4">
      <p className="text-xs font-medium mb-2" style={{ color: "var(--admin-text-tertiary)" }}>{label}</p>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-2">
            <span className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>{row.key}</span>
            <span className="text-sm font-bold" style={{ color: "var(--admin-text-primary)" }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminSystem() {
  const [activeService, setActiveService] = useState<ServiceInfo | null>(null);
  const [activeResourceService, setActiveResourceService] = useState<ServiceInfo | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [streamError, setStreamError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const outputRef = useRef<HTMLDivElement | null>(null);
  const closingRef = useRef(false);

  const { data: system, isLoading, refetch } = useAdminSystem();
  const { data: totalResources, isLoading: isTotalLoading, refetch: refetchTotal } = useAdminSystemResources();
  const {
    data: resources,
    isLoading: isResourcesLoading,
    refetch: refetchResources,
  } = useAdminServiceResources(activeResourceService?.key ?? null);

  // Close any active stream + clear state
  const closeDrawer = () => {
    closingRef.current = true;
    esRef.current?.close();
    esRef.current = null;
    setActiveService(null);
    setLines([]);
    setStreamError(null);
  };

  const closeResourceDrawer = () => {
    setActiveResourceService(null);
  };

  // Auto-scroll to the bottom while streaming
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [lines]);

  // Start streaming when a service is selected
  useEffect(() => {
    if (!activeService) return;

    setLines([]);
    setStreamError(null);
    closingRef.current = false;

    const es = new EventSource(sseUrl(activeService.key));
    esRef.current = es;

    es.addEventListener("error", (ev) => {
      // EventSource fires an "error" on any network failure and when the
      // server closes the stream. Only surface a message if the connection
      // is actually closed, and never when we closed it on purpose.
      if (!closingRef.current && es.readyState === EventSource.CLOSED) {
        setStreamError((prev) => prev ?? "اتصال به استریم لاگ قطع شد.");
      }
    });

    es.addEventListener("log", (ev) => {
      const payload = (ev as MessageEvent).data;
      setLines((prev) => [...prev.slice(-1000), payload]);
    });

    es.addEventListener("system-error", (ev) => {
      const payload = (ev as MessageEvent).data;
      setStreamError(payload);
      closingRef.current = true;
      es.close();
      esRef.current = null;
    });

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [activeService?.key]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>سیستم</h1>
        <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>وضعیت سرویس‌ها و سلامت سیستم</p>
      </div>

      {/* Total Resource Usage — refreshed on demand */}
      <div className="admin-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <h2 className="admin-card-title">مصرف منابع کل سرویس‌ها</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium" style={{ color: "var(--admin-text-secondary)" }}>
              {totalResources?.running
                ? `${toPersianDigits(totalResources.container_count)} کانتینر فعال`
                : "—"}
            </span>
            <button
              onClick={() => refetchTotal()}
              disabled={isTotalLoading}
              className="admin-btn admin-btn-secondary admin-btn-sm"
              aria-label="بروزرسانی منابع"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <path d="M21 3v6h-6" />
              </svg>
              {isTotalLoading ? "در حال دریافت..." : "بروزرسانی"}
            </button>
          </div>
        </div>

        {isTotalLoading ? (
          <div className="flex items-center justify-center py-10">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : totalResources && !totalResources.running ? (
          <div className="rounded-2xl p-5 text-sm" style={{ background: "var(--admin-warning-bg)", color: "var(--admin-warning)" }}>
            {totalResources.message || "هیچ کانتینری در حال اجرا نیست."}
          </div>
        ) : totalResources ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="پردازنده (CPU)"
              value={formatPercent(totalResources.cpu_percent)}
              percent={totalResources.cpu_percent}
              hint="مصرف لحظه‌ای"
            />
            <MetricCard
              label="حافظه (RAM)"
              value={totalResources.memory_used && totalResources.memory_limit
                ? `${totalResources.memory_used} از ${totalResources.memory_limit}`
                : "—"}
              percent={totalResources.memory_percent ?? null}
              hint={typeof totalResources.memory_percent === "number"
                ? `درصد استفاده: ${formatPercent(totalResources.memory_percent)}`
                : undefined}
            />
            <PairCard
              label="شبکه"
              rows={[
                { key: "دریافتی ↓", value: formatBytes(totalResources.network_rx_bytes) },
                { key: "ارسال ↑", value: formatBytes(totalResources.network_tx_bytes) },
              ]}
            />
            <PairCard
              label="دیسک (Block I/O)"
              rows={[
                { key: "خواندن", value: formatBytes(totalResources.block_read_bytes) },
                { key: "نوشتن", value: formatBytes(totalResources.block_write_bytes) },
              ]}
            />
          </div>
        ) : (
          <div className="rounded-2xl p-5 text-sm" style={{ background: "var(--admin-error-bg)", color: "var(--admin-error)" }}>
            خطا در دریافت اطلاعات منابع سیستم.
          </div>
        )}
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="admin-card p-5">
          <p className="text-xs font-medium mb-1" style={{ color: "var(--admin-text-tertiary)" }}>وضعیت کلی</p>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: overallStatusLabel(system?.status || "unknown").color }} />
            <span className="text-lg font-bold" style={{ color: "var(--admin-text-primary)" }}>
              {isLoading ? "در حال بررسی..." : overallStatusLabel(system?.status || "unknown").label}
            </span>
          </div>
        </div>
        <div className="admin-card p-5">
          <p className="text-xs font-medium mb-1" style={{ color: "var(--admin-text-tertiary)" }}>نسخه</p>
          <span className="text-lg font-bold" style={{ color: "var(--admin-text-primary)" }}>
            {system?.version ? `v${system.version}` : "—"}
          </span>
        </div>
        <div className="admin-card p-5">
          <p className="text-xs font-medium mb-1" style={{ color: "var(--admin-text-tertiary)" }}>بررسی مجدد</p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium" style={{ color: "var(--admin-text-secondary)" }}>
              {isLoading ? "در حال بررسی..." : "وضعیت لحظه‌ای سرویس‌ها"}
            </span>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="admin-btn admin-btn-secondary admin-btn-sm"
              aria-label="بررسی مجدد"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <path d="M21 3v6h-6" />
              </svg>
              بررسی مجدد
            </button>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">سرویس‌ها</h2>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            {services.map((service) => {
              const health: ServiceHealth | undefined = system?.services?.[service.key];
              const status = health?.status || "unknown";
              const label = health?.label || "نامشخص";
              const uptime = health?.uptime;
              const containerCount = health?.container_count ?? 0;
              const colors = statusColor(status);
              return (
                <div
                  key={service.key}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-2xl cursor-pointer transition-opacity hover:opacity-85"
                  style={{ background: colors.bg }}
                  onClick={() => setActiveResourceService(service)}
                >
                  <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: colors.dot }} />
                    <span className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2" strokeLinecap="round">
                        {service.icon}
                      </svg>
                      {service.name}
                    </span>
                    {containerCount > 1 && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full"
                        style={{ color: "var(--admin-text-secondary)", background: "var(--admin-bg)" }}>
                        {containerCount} نسخه
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
                    <span className="text-xs whitespace-nowrap" style={{ color: colors.text }}>{label}</span>
                    <span className="text-xs whitespace-nowrap" style={{ color: "var(--admin-text-tertiary)" }}>
                      {uptime ? `آپتایم: ${uptime}` : "—"}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveResourceService(service); }}
                      className="admin-btn admin-btn-secondary admin-btn-sm flex-1 sm:flex-none justify-center whitespace-nowrap"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M12 20v-6M12 14V6" />
                        <path d="M8 10l4-4 4 4" />
                        <path d="M3 17h18" />
                      </svg>
                      منابع
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveService(service); }}
                      className="admin-btn admin-btn-secondary admin-btn-sm flex-1 sm:flex-none justify-center whitespace-nowrap"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M4 17l6 6L20 13" />
                        <path d="M12 20h9" />
                        <path d="M2 3l4-4 4 4" />
                      </svg>
                      مشاهده لاگ
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Log Drawer — slides in from the left */}
      {activeService && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
            onClick={closeDrawer}
          />

          {/* Drawer panel */}
          <aside
            className="absolute top-0 bottom-0 left-0 w-full md:w-[560px] flex flex-col overflow-hidden animate-drawer-left"
            style={{ background: "var(--admin-bg-surface)" }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between"
              style={{ background: "var(--admin-bg-surface)", borderColor: "var(--admin-border)" }}>
              <div className="flex items-center gap-3">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: statusColor(system?.services?.[activeService.key]?.status || "unknown").dot }}
                />
                <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                  لاگ سرویس {activeService.name}
                </h2>
                <span className="flex items-center gap-1.5 text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: "var(--admin-primary)" }} />
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "var(--admin-primary)" }} />
                  </span>
                  زنده
                </span>
              </div>
              <button
                onClick={closeDrawer}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
                aria-label="بستن"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Terminal body */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div
                className="flex-1 overflow-y-auto p-4"
                ref={outputRef}
                dir="ltr"
                style={{
                  background: "#0d1117",
                  fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', monospace",
                  fontSize: "12.5px",
                  lineHeight: 1.6,
                }}
              >
                {/* Title bar */}
                <div className="flex items-center gap-1.5 px-1 pb-3 mb-3 border-b"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                  <span className="w-3 h-3 rounded-full" style={{ background: "#ff5f56" }} />
                  <span className="w-3 h-3 rounded-full" style={{ background: "#ffbd2e" }} />
                  <span className="w-3 h-3 rounded-full" style={{ background: "#27c93f" }} />
                  <span className="ml-2 text-[11px]" style={{ color: "#8b949e" }}>
                    {activeService.name.replace(/-/g, " ")} — docker compose logs
                  </span>
                </div>

                {streamError ? (
                  <div className="px-1 py-2" style={{ color: "#f85149" }}>
                    <span className="font-semibold">[خطا] </span>
                    {streamError}
                  </div>
                ) : lines.length === 0 ? (
                  <div className="px-1 py-2" style={{ color: "#8b949e" }}>
                    در حال اتصال به استریم لاگ...
                  </div>
                ) : (
                  <pre className="whitespace-pre-wrap break-words" style={{ color: "#c9d1d9" }}>
                    {lines.join("\n")}
                  </pre>
                )}

                {/* Cursor line */}
                <div className="flex items-center gap-2 px-1 pt-1">
                  <span style={{ color: "#3fb950" }}>asha@system</span>
                  <span style={{ color: "#8b949e" }}>~/$</span>
                  <span className="inline-block w-2 h-4 animate-pulse" style={{ background: "#c9d1d9" }} />
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Resources Drawer — slides in from the left */}
      {activeResourceService && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
            onClick={closeResourceDrawer}
          />

          {/* Drawer panel */}
          <aside
            className="absolute top-0 bottom-0 left-0 w-full md:w-[560px] flex flex-col overflow-hidden animate-drawer-left"
            style={{ background: "var(--admin-bg-surface)" }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between"
              style={{ background: "var(--admin-bg-surface)", borderColor: "var(--admin-border)" }}>
              <div className="flex items-center gap-3">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: statusColor(system?.services?.[activeResourceService.key]?.status || "unknown").dot }}
                />
                <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                  منابع سرویس {activeResourceService.name}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => refetchResources()}
                  disabled={isResourcesLoading}
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  aria-label="بروزرسانی منابع"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                    <path d="M21 3v6h-6" />
                  </svg>
                  بروزرسانی
                </button>
                <button
                  onClick={closeResourceDrawer}
                  className="p-1 rounded-lg transition-colors"
                  style={{ color: "var(--admin-text-tertiary)" }}
                  aria-label="بستن"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {isResourcesLoading ? (
                <div className="flex items-center justify-center py-16">
                  <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : resources && !resources.running ? (
                <div className="rounded-2xl p-5 text-sm" style={{ background: "var(--admin-warning-bg)", color: "var(--admin-warning)" }}>
                  {resources.message || "سرویس در حال اجرا نیست."}
                </div>
              ) : resources ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <MetricCard
                      label="پردازنده (CPU)"
                      value={formatPercent(resources.cpu_percent)}
                      percent={resources.cpu_percent}
                      hint="مصرف لحظه‌ای"
                    />
                    <MetricCard
                      label="حافظه (RAM)"
                      value={resources.memory_used && resources.memory_limit
                        ? `${resources.memory_used} از ${resources.memory_limit}`
                        : "—"}
                      percent={resources.memory_percent ?? null}
                      hint={typeof resources.memory_percent === "number"
                        ? `درصد استفاده: ${formatPercent(resources.memory_percent)}`
                        : undefined}
                    />
                  </div>

                  <div className="admin-card p-4">
                    <p className="text-xs font-medium mb-2" style={{ color: "var(--admin-text-tertiary)" }}>شبکه</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-xl p-3" style={{ background: "var(--admin-bg-hover)" }}>
                        <span className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>دریافتی ↓</span>
                        <p className="mt-1 font-bold" style={{ color: "var(--admin-text-primary)" }}>
                          {formatBytes(resources.network_rx_bytes)}
                        </p>
                      </div>
                      <div className="rounded-xl p-3" style={{ background: "var(--admin-bg-hover)" }}>
                        <span className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>ارسال ↑</span>
                        <p className="mt-1 font-bold" style={{ color: "var(--admin-text-primary)" }}>
                          {formatBytes(resources.network_tx_bytes)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="admin-card p-4">
                    <p className="text-xs font-medium mb-2" style={{ color: "var(--admin-text-tertiary)" }}>دیسک (Block I/O)</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-xl p-3" style={{ background: "var(--admin-bg-hover)" }}>
                        <span className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>خواندن</span>
                        <p className="mt-1 font-bold" style={{ color: "var(--admin-text-primary)" }}>
                          {formatBytes(resources.block_read_bytes)}
                        </p>
                      </div>
                      <div className="rounded-xl p-3" style={{ background: "var(--admin-bg-hover)" }}>
                        <span className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>نوشتن</span>
                        <p className="mt-1 font-bold" style={{ color: "var(--admin-text-primary)" }}>
                          {formatBytes(resources.block_write_bytes)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <MetricCard
                      label="پردازه‌ها (PIDs)"
                      value={resources.pids > 0 ? toPersianDigits(resources.pids) : "—"}
                    />
                    <MetricCard
                      label="کانتینرهای فعال"
                      value={resources.container_count > 0 ? toPersianDigits(resources.container_count) : "—"}
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl p-5 text-sm" style={{ background: "var(--admin-error-bg)", color: "var(--admin-error)" }}>
                  خطا در دریافت اطلاعات منابع سرویس.
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}